<template>
    <div class="zsh-feedback-wrapper">
        <div class="zsh-feedback-container">
            <v-expansion-panels>
                <v-expansion-panel>
                    <v-expansion-panel-title class="pl-1 width-auto text-primary text-capitalize">
                        {{ $t('filters') }}
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-row class="py-4">
                            <v-col md="8" cols="12" class="filters-form">
                                <v-row>
                                    <v-col md="6" cols="12">
                                        <order-service-daterange
                                            :start-date-label="$t('travel_date_from')"
                                            :end-date-label="$t('travel_date_to')"
                                            :outlined="false"
                                            :single-line="false"
                                            :no-title="true"
                                            clearable
                                            dense
                                            :_start-date="filters.travelDateFrom"
                                            :_end-date="filters.travelDateTo"
                                            @change-start-date="filters.travelDateFrom = $event"
                                            @change-end-date="filters.travelDateTo = $event"
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <v-select
                                            v-model="filters.status"
                                            :items="statuses"
                                            :label="$t('status')"
                                            clearable
                                            :item-title="'title'"
                                            :item-value="'value'"
                                            density="compact"
                                            hide-details
                                        />
                                    </v-col>
                                    <v-col md="6" cols="12">
                                        <order-service-daterange
                                            :start-date-label="$t('created_date_from')"
                                            :end-date-label="$t('created_date_to')"
                                            :outlined="false"
                                            :single-line="false"
                                            :no-title="true"
                                            clearable
                                            dense
                                            :_start-date="filters.createdDateFrom"
                                            :_end-date="filters.createdDateTo"
                                            @change-start-date="filters.createdDateFrom = $event"
                                            @change-end-date="filters.createdDateTo = $event"
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <private-client-and-companies-autocomplete
                                            v-model="selectedClient"
                                            :show-icon="false"
                                            :label="$t('client_name')"
                                            :relationship="['CLIENT']"
                                            :rules="[]"
                                            dense
                                            @change="onClientNameFilterChange"
                                        />
                                    </v-col>
                                    <v-col md="6" cols="12">
                                        <order-service-daterange
                                            :start-date-label="$t('modified_date_from')"
                                            :end-date-label="$t('modified_date_to')"
                                            :outlined="false"
                                            :single-line="false"
                                            :no-title="true"
                                            clearable
                                            dense
                                            :_start-date="filters.modifiedDateFrom"
                                            :_end-date="filters.modifiedDateTo"
                                            @change-start-date="filters.modifiedDateFrom = $event"
                                            @change-end-date="filters.modifiedDateTo = $event"
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <v-text-field
                                            v-model="filters.serviceName"
                                            :label="$t('service_name')"
                                            density="compact"
                                            hide-details
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <v-select
                                            v-model="filters.travellerTypeCode"
                                            :items="travellerTypes"
                                            :label="$t('reviews.traveller_type')"
                                            clearable
                                            :item-title="'title'"
                                            :item-value="'value'"
                                            density="compact"
                                            hide-details
                                        />
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-spacer />
                            <v-col md="2" cols="12">
                                <v-row>
                                    <v-col cols="6">
                                        <v-btn
                                            block
                                            :color="isSomeFiltersSelected ? 'primary' : 'white'"
                                            size="small"
                                            @click="getFeedbacks"
                                        >
                                            {{ $t('filter') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-btn color="white" block size="small" @click="resetFilters">
                                            {{ $t('reset') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <v-data-table
                v-if="feedbacks"
                :headers="headers"
                :sort-by="[{key: 'created', order: 'desc'}]"
                :disable-sort="isMobile"
                :items="feedbacks"
                :loading="feedbacksLoading"
                class="zsh-reviews-table"
                show-expand
                :item-key="'id'"
                :expanded="showFeedbackRows"
                @update:expanded="showFeedbackRows = $event"
            >
                <template v-slot:header.data-table-expand>
                    <span>{{ $t('reviews.guest_feedback') }}</span>
                </template>
                <template v-slot:item.serviceName="{item}">
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">{{ item.serviceName }}</span>
                        <span v-if="item.cityName && item.countryName" class="text-caption text-grey">
                            {{ `${item.cityName}, ${item.countryName}` }}
                        </span>
                    </div>
                </template>
                <template v-slot:item.reviewScore="{item}">
                    <div class="text-center">
                        <v-chip
                            v-if="item.reviewScore !== null && item.reviewScore !== undefined"
                            :style="{backgroundColor: getScoreBackgroundColor(item.reviewScore), color: 'white'}"
                            label
                            size="small"
                        >
                            {{ item.reviewScore }}
                        </v-chip>
                        <span v-else>{{ $t('none') }}</span>
                    </div>
                </template>
                <template v-slot:item.reviewSustainableScore="{item}">
                    <div class="text-center">
                        <v-chip
                            v-if="item.reviewSustainableScore !== null && item.reviewSustainableScore !== undefined"
                            :style="{
                                backgroundColor: getScoreBackgroundColor(item.reviewSustainableScore),
                                color: 'white',
                            }"
                            label
                            size="small"
                        >
                            {{ item.reviewSustainableScore }}
                        </v-chip>
                        <span v-else>{{ $t('none') }}</span>
                    </div>
                </template>

                <template v-slot:item.startServiceDate="{item}">
                    {{ `${formatDateOnly(item.startServiceDate)} - ${formatDateOnly(item.endServiceDate)}` }}
                </template>
                <template v-slot:item.reviewTravellerTypeCode="{item}">
                    <span v-if="item.reviewTravellerTypeCode">
                        {{ $t(`travellerType.${item.reviewTravellerTypeCode}`) }}
                    </span>
                    <span v-else class="text--secondary">
                        {{ $t('none') }}
                    </span>
                </template>
                <template v-slot:item.status="{item}">
                    <div v-if="item.id !== feedbackStatusEditId" class="d-flex align-center">
                        <span>
                            {{ $t(`review_status.${item.status}`) }}
                        </span>
                        <v-btn size="small" icon="mdi-pencil" variant="text" @click="feedbackStatusEditId = item.id" />
                    </div>
                    <div v-else style="width: 150px" class="d-flex align-center">
                        <v-select
                            density="compact"
                            item-title="title"
                            item-value="value"
                            :items="statuses"
                            variant="outlined"
                            hide-details
                            :disabled="item.id === feedbackStatusLoadingId"
                            :loading="item.id === feedbackStatusLoadingId"
                            :model-value="item.status"
                            @update:model-value="changeStatus(item, $event)"
                        />
                    </div>
                </template>

                <template v-slot:item.data-table-expand="{item}">
                    <div v-if="hasFeedback(item)" class="d-flex align-center justify-center">
                        <span class="text-primary mr-2 cursor-pointer" @click="toggleFeedbackRow(item)">
                            {{ isFeedbackRowVisible(item) ? $t('hide') : $t('show') }}
                        </span>
                        <v-btn icon variant="text" size="small" @click="toggleFeedbackRow(item)">
                            <v-icon>
                                {{ isFeedbackRowVisible(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                        </v-btn>
                    </div>
                    <div v-else class="d-flex justify-center">
                        <span class="text-grey">{{ $t('none') }}</span>
                    </div>
                </template>

                <!-- Expanded content template -->
                <template v-slot:expanded-row="{item}">
                    <tr>
                        <td :colspan="headers.length + 1" class="pa-0">
                            <div class="expanded-feedback-content pa-4 bg-grey-lighten-5">
                                <div v-if="item.positiveFeedback" class="feedback-editor">
                                    <div class="feedback-input-row">
                                        <textarea
                                            class="feedback-content"
                                            :value="editedFeedbacks[item.id] || item.positiveFeedback"
                                            rows="3"
                                            maxlength="1000"
                                            @input="onFeedbackInput(item, $event)"
                                        />
                                        <v-btn
                                            size="small"
                                            color="primary"
                                            :disabled="!canSaveFeedback(item)"
                                            @click="saveFeedback(item)"
                                        >
                                            {{ $t('save') }}
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Watch} from 'vue-facing-decorator'
    import OrderServiceDaterange from '@/components/account/filters/OrderServiceDaterange.vue'
    import PrivateClientAndCompaniesAutocomplete from '../../search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import OrdersPriceLabel from '@/components/account/snippets/OrdersPriceLabel.vue'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'

    interface FeedbackItem {
        id: string
        serviceName: string
        cityName?: string
        countryName?: string
        reviewScore?: number
        reviewSustainableScore?: number
        startServiceDate: string
        endServiceDate: string
        reviewTravellerTypeCode?: string
        status: string
        positiveFeedback?: string
        clientName: string
        created: string
        reservationNumber: string
    }

    interface FilterOptions {
        travelDateFrom?: string
        travelDateTo?: string
        status?: string
        createdDateFrom?: string
        createdDateTo?: string
        modifiedDateFrom?: string
        modifiedDateTo?: string
        serviceName?: string
        travellerTypeCode?: string
        clientId?: string
    }

    interface SelectOption {
        value: string
        title: string
    }

    interface ClientSelection {
        personId: string
    }

    @Component({
        components: {
            OrdersPriceLabel,
            PrivateClientAndCompaniesAutocomplete,
            OrderServiceDaterange,
            Breadcrumbs,
        },
    })
    export class FeedbackList extends Vue {
        feedbacks: FeedbackItem[] = []
        feedbacksLoading = false
        feedbackStatusLoadingId: string | null = null
        feedbackStatusEditId: string | null = null
        expandedItems: string[] = []
        showFeedbackRows: string[] = []
        editedFeedbacks: Record<string, string> = {}
        selectedClient: ClientSelection | null = null

        filters: FilterOptions = {}

        @Watch('filters', {deep: true})
        onFiltersChange() {
            // Clean up empty values without reassigning the whole object to avoid infinite loop
            Object.keys(this.filters).forEach(key => {
                const value = this.filters[key as keyof FilterOptions]
                if (value === undefined || value === null || value === '') {
                    Reflect.deleteProperty(this.filters, key)
                }
            })
        }

        async mounted() {
            await this.getFeedbacks()
        }

        async getFeedbacks() {
            try {
                this.feedbacksLoading = true
                this.feedbacks = (
                    await this.$api.reviews.get({
                        ...this.filters,
                    })
                ).reviews
            } catch {
                // Handle error silently
            } finally {
                this.feedbacksLoading = false
            }
        }

        async changeStatus(item: FeedbackItem, newStatus: string) {
            try {
                this.feedbackStatusLoadingId = item.id
                const rs = await this.$api.reviews.put(item.id, {
                    status: newStatus,
                })
                item.status = rs.status
                this.feedbackStatusEditId = null
            } catch {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.feedbackStatusLoadingId = null
            }
        }

        onClientNameFilterChange(val: ClientSelection | null) {
            this.selectedClient = val
            this.filters = {...this.filters, clientId: val?.personId}
        }

        resetFilters() {
            this.filters = {}
            this.selectedClient = null
            this.getFeedbacks()
        }

        get headers() {
            if (this.isMobile) {
                return [{value: 'mobile'}]
            }

            return [
                {title: 'Date/Time', value: 'created'},
                {title: 'Client', value: 'clientName'},
                {title: 'Service', value: 'serviceName'},
                {title: 'Basic score', value: 'reviewScore', align: 'center' as const},
                {title: 'Sustainability score', value: 'reviewSustainableScore', align: 'center' as const},

                {title: 'Travel dates', value: 'startServiceDate'},
                {title: 'Reservation number', value: 'reservationNumber'},
                {title: this.$t('reviews.traveller_type'), value: 'reviewTravellerTypeCode'},
                {title: 'Status', value: 'status'},
            ]
        }

        get statuses(): SelectOption[] {
            return [
                {value: 'DECLINED', title: this.$t('review_status.DECLINED')},
                {value: 'ON_REVIEW', title: this.$t('review_status.ON_REVIEW')},
                {value: 'PUBLISHED', title: this.$t('review_status.PUBLISHED')},
            ]
        }

        get travellerTypes(): SelectOption[] {
            return [
                {value: 'SOLO', title: this.$t('travellerType.SOLO')},
                {value: 'COUPLE', title: this.$t('travellerType.COUPLE')},
                {value: 'FAMILY', title: this.$t('travellerType.FAMILY')},
                {value: 'GROUP', title: this.$t('travellerType.GROUP')},
            ]
        }

        get isSomeFiltersSelected() {
            return Object.keys(this.filters).length !== 0
        }

        get isMobile(): boolean {
            return (this.$breakpoint as any)?.smAndDown || false
        }

        getColor(value: number) {
            // Use a more reliable color scheme with better contrast
            if (value <= 0.3) return '#d32f2f' // Red for low scores
            if (value <= 0.5) return '#f57c00' // Orange for medium-low scores
            if (value <= 0.7) return '#fbc02d' // Yellow for medium scores
            return '#388e3c' // Green for high scores
        }

        getScoreColor(score: number) {
            // Convert score (1-10) to color
            if (score <= 3) return 'red'
            if (score <= 5) return 'orange'
            if (score <= 7) return 'amber'
            return 'green'
        }

        getScoreBackgroundColor(score: number): string {
            if (score === 0 || score >= 8) return '#388e3c' // Dark green
            if (score >= 5) return '#fbc02d' // Dark yellow
            if (score >= 3) return '#f57c00' // Dark orange
            return '#d32f2f' // Dark red
        }

        hasFeedback(item: FeedbackItem): boolean {
            return !!item.positiveFeedback
        }

        toggleFeedbackRow(item: FeedbackItem) {
            const index = this.showFeedbackRows.findIndex(id => id === item.id)
            if (index > -1) {
                this.showFeedbackRows.splice(index, 1)
                Reflect.deleteProperty(this.editedFeedbacks, item.id)
            } else {
                this.showFeedbackRows.push(item.id)
                this.editedFeedbacks[item.id] = item.positiveFeedback || ''
            }
        }

        isFeedbackRowVisible(item: FeedbackItem): boolean {
            return this.showFeedbackRows.some(id => id === item.id)
        }

        onFeedbackInput(item: FeedbackItem, event: Event) {
            const target = event.target as HTMLTextAreaElement
            this.editedFeedbacks[item.id] = target.value
        }

        canSaveFeedback(item: FeedbackItem): boolean {
            const edited = this.editedFeedbacks[item.id]
            return edited && edited.trim() !== '' && edited !== item.positiveFeedback
        }

        async saveFeedback(item: FeedbackItem) {
            const newFeedback = this.editedFeedbacks[item.id]
            try {
                await this.$api.reviews.put(item.id, {feedback: newFeedback})
                item.positiveFeedback = newFeedback
                Reflect.deleteProperty(this.editedFeedbacks, item.id)
                this.$toast.success(this.$t('feedback_updated'))
            } catch {
                this.$toast.error(this.$t('error_message.save_error'))
            }
        }

        truncateText(text: string, maxLength: number = 50) {
            if (!text) return ''
            return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
        }

        formatDate(dateString: string) {
            if (!dateString) return ''
            return this.$filters.dateTimeFormat(dateString)
        }

        formatDateOnly(dateString: string) {
            if (!dateString) return ''
            return this.$filters.dateShortFormat(dateString)
        }
    }

    export default FeedbackList
</script>

<style scoped lang="scss">
    .zsh-feedback-wrapper {
        // Minimal horizontal padding, no excessive spacing
        padding: 0 16px;
        width: 100%;
    }

    .zsh-feedback-container {
        // Remove card padding and add minimal spacing
        padding: 0;
    }

    .zsh-reviews-table {
        width: 100% !important;
        margin: 0 !important;

        :deep(.v-table) {
            width: 100% !important;
        }

        :deep(.v-data-table__wrapper) {
            width: 100% !important;
        }

        :deep(.v-data-table-header) {
            background-color: #009688; // ZSH teal color

            th {
                color: white !important;
                font-weight: bold;

                &.text-center {
                    text-align: center !important;
                }
            }
        }

        :deep(.v-chip) {
            font-weight: bold;

            .white--text {
                color: white !important;
            }
        }

        // Ensure proper centering for table cells
        :deep(tbody) {
            td.text-center {
                text-align: center !important;
                vertical-align: middle;
            }
        }
    }

    .feedback-cell {
        max-width: 200px;

        .feedback-text {
            display: block;
            word-break: break-word;
        }
    }

    .expanded-feedback-content {
        border-left: 3px solid #009688;
    }

    .feedback-editor {
        .feedback-input-row {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            .feedback-content {
                flex: 1;
                min-height: 60px;
                padding: 8px;
                border: 1px solid #ddd;
                border-radius: 4px;
                background-color: white;
                outline: none;
                resize: vertical;
                font-family: inherit;
                font-size: inherit;
                line-height: inherit;

                &:focus {
                    border-color: #009688;
                    box-shadow: 0 0 0 2px rgba(0, 150, 136, 0.2);
                }
            }
        }
    }

    // Style for expanded rows
    :deep(.v-data-table__expanded__content) {
        box-shadow: none !important;
    }

    :deep(.v-data-table__expanded__row) {
        background-color: #f5f5f5 !important;
    }

    @media (max-width: 960px) {
        :deep(.v-data-table__mobile-row__cell) {
            width: 100%;
        }

        :deep(.v-data-table__mobile-row) {
            padding: 0 !important;
        }
    }
</style>
